import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Users, Clock, AlertTriangle, CheckCircle, UserCog, Calendar, FileText, Zap, ChevronRight, Shield, Phone, Mail, MapPin, Briefcase, Camera, Trash2 } from 'lucide-react';

const roleColors = { manager: 'bg-gold/20 text-gold', director: 'bg-purple-500/20 text-purple-400', chef: 'bg-amber-500/20 text-amber-400', bartender: 'bg-blue-accent/20 text-blue-accent', shisha_expert: 'bg-pink-500/20 text-pink-400', waiter: 'bg-emerald-500/20 text-emerald-400' };
const empTypeColors = { 'full-time': 'bg-emerald-500/20 text-emerald-400', 'part-time': 'bg-blue-500/20 text-blue-400', 'casual': 'bg-amber-500/20 text-amber-400' };

export default function StaffManagementPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [adminForm, setAdminForm] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [saving, setSaving] = useState(false);

  const isManager = user?.role === 'manager' || user?.role === 'director';
  const isDirector = user?.role === 'director';

  // Director-only "Purge Staff by Email" tool. Two-step: dry-run
  // first (returns who'd be deleted + counts of related history),
  // then the director explicitly confirms to actually delete.
  // Backend: POST /api/staff/admin/purge-staff-by-email
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePattern, setPurgePattern] = useState('');
  const [purgePreview, setPurgePreview] = useState(null); // dry-run result
  const [purgeBusy, setPurgeBusy] = useState(false);

  const runPurgeDryRun = async () => {
    if (purgeBusy) return;
    const pattern = (purgePattern || '').trim().toLowerCase();
    if (!pattern || !pattern.includes('@') || pattern.length < 4) {
      toast.error('Pattern must include @ and be at least 4 characters');
      return;
    }
    setPurgeBusy(true);
    setPurgePreview(null);
    try {
      const { data } = await api.post('/staff/admin/purge-staff-by-email', { pattern, confirm: false });
      setPurgePreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not preview purge');
    } finally {
      setPurgeBusy(false);
    }
  };

  const confirmPurge = async (wipeHistory) => {
    if (purgeBusy || !purgePreview) return;
    const count = purgePreview.user_count || 0;
    if (!window.confirm(
      `FINAL CONFIRM:\n\nDelete ${count} staff account(s) matching "${purgePreview.pattern}"?\n\n` +
      (wipeHistory
        ? `This will ALSO wipe their clock_records, shifts, leave_requests, performance_scores and audit_log entries (${Object.values(purgePreview.related || {}).reduce((a, b) => a + b, 0)} related rows total).`
        : `Their historical records (clock_records, shifts, audit_log) will be PRESERVED so payroll/reports stay intact.`) +
      `\n\nDirectors are automatically excluded.\n\nProceed?`
    )) return;
    setPurgeBusy(true);
    try {
      const { data } = await api.post('/staff/admin/purge-staff-by-email', {
        pattern: purgePreview.pattern,
        confirm: true,
        also_wipe_history: !!wipeHistory,
      });
      const total = Object.values(data?.summary || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Deleted ${data?.summary?.users || 0} staff (+ ${total - (data?.summary?.users || 0)} related rows)`);
      setPurgePreview(null);
      setPurgePattern('');
      setPurgeOpen(false);
      fetchStaff();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurgeBusy(false);
    }
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const endpoint = isManager ? '/staff/dashboard/director' : '/staff/dashboard/my';
      const { data } = await api.get(endpoint);
      setDashboard(data);
    } catch (err) { console.error('[StaffManagementPage.js] error:', err); }
  }, [isManager]);

  const fetchStaff = useCallback(async () => {
    if (!isManager) return;
    try { const { data } = await api.get('/staff/profiles'); setStaffList(data); } catch (err) { console.error('[StaffManagementPage.js] error:', err); }
  }, [isManager]);

  const fetchAudit = useCallback(async () => {
    if (!isManager) return;
    try { const { data } = await api.get('/staff/audit-log'); setAuditLog(data); } catch (err) { console.error('[StaffManagementPage.js] error:', err); }
  }, [isManager]);

  // Feb 2026 owner ask: when face-match keeps rejecting a staff member
  // (haircut, glasses, accidental enrollment of the wrong face during
  // kiosk setup), the manager needs a one-tap "reset" so the next clock
  // attempt re-enrols their selfie as the fresh reference. Wires up the
  // existing `/api/staff/{user_id}/reset-reference-photo` backend.
  const resetReferencePhoto = useCallback(async (staff) => {
    if (!staff?.id) return;
    if (!window.confirm(
      `Reset face reference for ${staff.name || staff.email}?\n\nThe NEXT kiosk clock-in attempt by this staff member will store their fresh selfie as the new reference photo — useful after a haircut, accident, or if someone else's face got enrolled by mistake.`
    )) return;
    try {
      await api.post(`/staff/${staff.id}/reset-reference-photo`);
      toast.success(`Face reference cleared for ${staff.name || staff.email}. Their next clock-in will re-enrol.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset face reference');
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchStaff();
  }, [fetchDashboard, fetchStaff]);

  const openEditDialog = (staff) => {
    setSelectedStaff(staff);
    setAdminForm({
      // Display Name = the personal name shown when this staff clocks
      // in via the kiosk. Defaults to whatever's currently on the user
      // record so the director can see it clearly + tweak it.
      display_name: staff.name || '',
      department: staff.department || '',
      employment_type: staff.employment_type || 'casual',
      hourly_rate: staff.hourly_rate || 0,
      salary: staff.salary || 0,
      start_date: staff.start_date || '',
      end_date: staff.end_date || '',
      position_title: staff.position_title || '',
      is_active: staff.is_active !== false,
      notes: staff.notes || '',
    });
    setEditDialog(true);
  };

  const saveAdminUpdate = async () => {
    if (!selectedStaff) return;
    setSaving(true);
    try {
      // Feb 2026: persist the personal `display_name` on the user record
      // FIRST (it's what the kiosk welcome toast + roster board use).
      // Failing on either step shouldn't roll back the other — the user
      // can simply retry. We treat the name update as best-effort so a
      // payroll-only edit (no name change) doesn't fail just because
      // the display-name endpoint hiccups.
      const newName = (adminForm.display_name || '').trim();
      if (newName && newName !== (selectedStaff.name || '')) {
        try {
          await api.patch(`/staff/users/${selectedStaff.id}/display-name`, { display_name: newName });
        } catch (e) {
          console.error('[StaffManagement] display-name update failed', e);
          toast.error(e?.response?.data?.detail || 'Could not update display name');
        }
      }
      await api.put(`/staff/profiles/${selectedStaff.id}`, adminForm);
      toast.success('Staff profile updated');
      setEditDialog(false);
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const tabs = isManager
    ? [{ key: 'dashboard', label: 'Dashboard', icon: Zap }, { key: 'team', label: 'Team', icon: Users }, { key: 'audit', label: 'Audit Trail', icon: FileText }]
    : [{ key: 'dashboard', label: 'My Dashboard', icon: Zap }];

  return (
    <div className="animate-fadeIn">
      {/* Tabs */}
      <div className="flex gap-1.5 mb-6 border-b border-white/5 pb-3">
        {tabs.map(t => (
          <Button key={t.key} size="sm" variant={tab === t.key ? 'default' : 'ghost'}
            onClick={() => { setTab(t.key); if (t.key === 'audit') fetchAudit(); }}
            className={`h-8 rounded-sm font-sans text-xs ${tab === t.key ? 'bg-gold/10 text-gold border border-gold/20' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid={`staff-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
          </Button>
        ))}
      </div>

      {/* DASHBOARD TAB */}
      {tab === 'dashboard' && dashboard && isManager && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Users} label="On Shift Now" value={dashboard.on_shift_count} color="emerald" />
            <StatCard icon={AlertTriangle} label="Late Staff" value={dashboard.late_staff?.length || 0} color="red" />
            <StatCard icon={Clock} label="Labour Hours" value={`${dashboard.labour_hours_today}h`} color="blue" />
            <StatCard icon={CheckCircle} label="Pending Approvals" value={dashboard.pending_approvals?.total || 0} color="amber" />
          </div>

          {/* On Shift */}
          <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
            <h3 className="text-sm font-sans font-medium text-zinc-400 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Currently On Shift
            </h3>
            {dashboard.on_shift?.length > 0 ? (
              <div className="space-y-2">
                {dashboard.on_shift.map(s => (
                  <div key={s.id || s.user_id} className="flex items-center justify-between text-sm font-sans">
                    <span className="text-white">{s.user_name || s.user_id}</span>
                    <span className="text-zinc-500">Clocked in: {s.clock_in_time}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-zinc-600 font-sans text-sm">No one currently on shift</p>}
          </div>

          {/* Pending Approvals */}
          {dashboard.pending_approvals?.total > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-sm p-4">
              <h3 className="text-sm font-sans font-medium text-amber-400 mb-2">Pending Approvals</h3>
              <div className="grid grid-cols-3 gap-3 text-xs font-sans">
                <div><span className="text-zinc-500">Leave Requests:</span> <span className="text-white ml-1">{dashboard.pending_approvals.leave_requests}</span></div>
                <div><span className="text-zinc-500">Shift Swaps:</span> <span className="text-white ml-1">{dashboard.pending_approvals.shift_swaps}</span></div>
                <div><span className="text-zinc-500">Exceptions:</span> <span className="text-white ml-1">{dashboard.pending_approvals.exceptions}</span></div>
              </div>
            </div>
          )}

          {/* Today's Shifts */}
          <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
            <h3 className="text-sm font-sans font-medium text-zinc-400 mb-3">Today's Scheduled Shifts</h3>
            {dashboard.today_shifts?.length > 0 ? (
              <div className="space-y-2">
                {dashboard.today_shifts.map(s => (
                  <div key={s.id} className="flex items-center justify-between text-sm font-sans p-2 bg-white/[0.02] rounded-sm">
                    <div><span className="text-white">{s.user_name}</span> <Badge className={`${roleColors[s.role] || 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-[10px] ml-2`}>{s.role}</Badge></div>
                    <span className="text-zinc-400">{s.start_time} - {s.end_time}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-zinc-600 font-sans text-sm">No shifts scheduled today. Create shifts from the Roster section.</p>}
          </div>
        </div>
      )}

      {/* STAFF DASHBOARD (non-manager) */}
      {tab === 'dashboard' && dashboard && !isManager && (
        <div className="space-y-6">
          <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
            <h3 className="text-sm font-sans font-medium text-zinc-400 mb-3">Today's Shift</h3>
            {dashboard.today_shift ? (
              <div className="text-white font-sans">
                <p className="text-lg">{dashboard.today_shift.start_time} - {dashboard.today_shift.end_time}</p>
                <p className="text-zinc-500 text-sm">{dashboard.today_shift.shift_type || 'Standard'} Shift</p>
              </div>
            ) : <p className="text-zinc-600 font-sans text-sm">No shift scheduled today</p>}
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
            <h3 className="text-sm font-sans font-medium text-zinc-400 mb-3">Upcoming Shifts</h3>
            {dashboard.upcoming_shifts?.length > 0 ? (
              <div className="space-y-2">
                {dashboard.upcoming_shifts.slice(0, 7).map(s => (
                  <div key={s.id || s.date} className="flex justify-between text-sm font-sans">
                    <span className="text-white">{s.date}</span>
                    <span className="text-zinc-400">{s.start_time} - {s.end_time}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-zinc-600 font-sans text-sm">No upcoming shifts</p>}
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
            <h3 className="text-sm font-sans font-medium text-zinc-400 mb-3">Leave Balances</h3>
            <div className="grid grid-cols-3 gap-3">
              {['annual', 'sick', 'personal'].map(type => {
                const bal = dashboard.leave_balances || {};
                const total = bal[type] || 0;
                const used = bal[`used_${type}`] || 0;
                return (
                  <div key={type} className="text-center p-3 bg-white/[0.02] rounded-sm">
                    <p className="text-xl font-sans text-white font-semibold">{total - used}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">{type} left</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TEAM TAB */}
      {tab === 'team' && isManager && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-sans text-zinc-400">{staffList.length} staff members</p>
            {isDirector && (
              <Button
                size="sm"
                onClick={() => { setPurgeOpen(true); setPurgePreview(null); }}
                className="bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-200 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
                data-testid="purge-staff-btn"
                title="Bulk-delete staff accounts matching an email pattern (director only)"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Purge by Email
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {staffList.map(staff => (
              <div key={staff.id} className="bg-white/[0.03] border border-white/5 rounded-sm p-4 card-hover" data-testid={`staff-card-${staff.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-gold/10 flex items-center justify-center text-gold font-sans font-bold text-sm">
                        {staff.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-white font-sans font-medium text-sm">{staff.name}</p>
                        <p className="text-zinc-500 font-sans text-xs">{staff.email}</p>
                      </div>
                    </div>
                  </div>
                  <Badge className={`${roleColors[staff.role] || 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-[10px]`}>{staff.role}</Badge>
                </div>
                <div className="space-y-1 text-xs font-sans">
                  {staff.position_title && <div className="flex items-center gap-1.5 text-zinc-400"><Briefcase className="w-3 h-3" />{staff.position_title}</div>}
                  {staff.phone && <div className="flex items-center gap-1.5 text-zinc-400"><Phone className="w-3 h-3" />{staff.phone}</div>}
                  {staff.employment_type && <Badge className={`${empTypeColors[staff.employment_type] || 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-[10px]`}>{staff.employment_type}</Badge>}
                  {staff.hourly_rate > 0 && <span className="text-zinc-500 ml-2">${staff.hourly_rate}/hr</span>}
                </div>
                <div className="flex justify-end pt-2 mt-2 border-t border-white/5 gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => resetReferencePhoto(staff)}
                    className="h-7 text-zinc-400 hover:text-amber-300 font-sans text-xs"
                    data-testid={`reset-face-${staff.id}`}
                    title="Clear stored face — next clock-in re-enrols"
                  >
                    <Camera className="w-3 h-3 mr-1" /> Reset Face
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEditDialog(staff)} className="h-7 text-gold hover:text-gold/80 font-sans text-xs" data-testid={`edit-staff-${staff.id}`}>
                    <UserCog className="w-3 h-3 mr-1" /> Manage
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AUDIT TAB */}
      {tab === 'audit' && isManager && (
        <div className="space-y-2">
          <p className="text-sm font-sans text-zinc-400 mb-2">{auditLog.length} audit entries</p>
          {auditLog.length === 0 ? (
            <p className="text-zinc-600 font-sans text-sm py-8 text-center">No audit entries yet</p>
          ) : (
            <div className="space-y-1">
              {auditLog.map(entry => (
                <div key={entry.id} className="p-3 bg-white/[0.03] border border-white/5 rounded-sm text-xs font-sans">
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge className="bg-zinc-500/20 text-zinc-400 border-0 text-[10px] mr-2">{entry.category}</Badge>
                      <span className="text-zinc-300">{entry.field}</span>
                    </div>
                    <span className="text-zinc-600">{new Date(entry.timestamp).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {entry.old_value && entry.old_value !== '' && <p className="text-red-400/60 mt-1">- {entry.old_value}</p>}
                  <p className="text-emerald-400/60">+ {entry.new_value}</p>
                  {entry.reason && <p className="text-zinc-500 mt-1">Reason: {entry.reason}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* EDIT DIALOG */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <UserCog className="w-5 h-5 text-gold" /> {selectedStaff?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Personal display name — what the kiosk welcome toast +
                roster board show. Top of the dialog so the director
                sees it before any payroll/banking fields. */}
            <div className="space-y-2 p-3 bg-gold/[0.04] border border-gold/15 rounded-sm">
              <Label className="text-xs uppercase tracking-[0.15em] text-gold/80 font-sans">
                Display Name · shown on Kiosk
              </Label>
              <Input
                value={adminForm.display_name || ''}
                onChange={(e) => setAdminForm((p) => ({ ...p, display_name: e.target.value }))}
                placeholder="e.g. Yogesh Dhawan"
                className="bg-transparent border border-gold/30 text-white font-sans"
                data-testid="staff-display-name"
              />
              <p className="text-[10px] font-sans text-zinc-500">
                Personal name (e.g. <span className="text-zinc-300">Aaryan</span> instead of <span className="text-zinc-300">Waiter</span>). Used by the kiosk welcome and live roster board.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Position Title</Label>
                <Input value={adminForm.position_title || ''} onChange={e => setAdminForm(p => ({ ...p, position_title: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-position" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Department</Label>
                <Select value={adminForm.department || ''} onValueChange={v => setAdminForm(p => ({ ...p, department: v }))}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-department"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">
                    {['Bar', 'Kitchen', 'Shisha', 'Floor', 'Management', 'Security', 'Events'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Employment Type</Label>
                <Select value={adminForm.employment_type} onValueChange={v => setAdminForm(p => ({ ...p, employment_type: v }))}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-emp-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">
                    <SelectItem value="full-time">Full Time</SelectItem>
                    <SelectItem value="part-time">Part Time</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Hourly Rate ($)</Label>
                <Input type="number" step="0.01" value={adminForm.hourly_rate || ''} onChange={e => setAdminForm(p => ({ ...p, hourly_rate: parseFloat(e.target.value) || 0 }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-rate" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Start Date</Label>
                <Input type="date" value={adminForm.start_date || ''} onChange={e => setAdminForm(p => ({ ...p, start_date: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-start-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">End Date</Label>
                <Input type="date" value={adminForm.end_date || ''} onChange={e => setAdminForm(p => ({ ...p, end_date: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" />
              </div>
            </div>

            <div className="border-t border-white/5 pt-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-2">Banking & Tax</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">BSB</Label>
                  <Input value={adminForm.bank_bsb || ''} onChange={e => setAdminForm(p => ({ ...p, bank_bsb: e.target.value }))} placeholder="e.g. 062-001" className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-bsb" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Account #</Label>
                  <Input value={adminForm.bank_account || ''} onChange={e => setAdminForm(p => ({ ...p, bank_account: e.target.value }))} placeholder="e.g. 12345678" className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-account" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Super Fund</Label>
                  <Input value={adminForm.superannuation_fund || ''} onChange={e => setAdminForm(p => ({ ...p, superannuation_fund: e.target.value }))} placeholder="e.g. AustralianSuper" className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-super" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Tax Rate Override (%)</Label>
                  <Input type="number" step="0.5" min="0" max="100" value={adminForm.tax_rate_override !== undefined && adminForm.tax_rate_override !== null ? (adminForm.tax_rate_override * 100) : ''} onChange={e => { const v = e.target.value; setAdminForm(p => ({ ...p, tax_rate_override: v === '' ? null : (parseFloat(v) || 0) / 100 })); }} placeholder="Auto (PAYG bracket)" className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-tax-override" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Notes</Label>
              <textarea value={adminForm.notes || ''} onChange={e => setAdminForm(p => ({ ...p, notes: e.target.value }))} className="w-full bg-transparent border border-white/10 text-white font-sans text-sm rounded-sm p-3 min-h-[60px] focus:border-gold/50 focus:outline-none resize-none" data-testid="staff-notes" />
            </div>
            <Button onClick={saveAdminUpdate} disabled={saving} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-staff-profile">
              {saving ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PURGE STAFF BY EMAIL — director only */}
      <Dialog open={purgeOpen} onOpenChange={(o) => { setPurgeOpen(o); if (!o) setPurgePreview(null); }}>
        <DialogContent className="bg-bg-secondary border border-rose-500/30 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-rose-200 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Purge Staff by Email Pattern
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] font-sans text-zinc-400 mb-3">
            Bulk-delete every non-director staff whose email contains the substring you enter. Step 1: dry-run preview. Step 2: explicit confirm. Directors are always excluded.
          </p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Email pattern</Label>
              <div className="flex gap-2">
                <Input
                  value={purgePattern}
                  onChange={(e) => setPurgePattern(e.target.value)}
                  placeholder="@qave.com.au"
                  className="bg-transparent border-white/10 text-white font-mono"
                  data-testid="purge-pattern-input"
                />
                <Button
                  onClick={runPurgeDryRun}
                  disabled={purgeBusy || !purgePattern.trim()}
                  className="bg-white/10 hover:bg-white/20 border border-white/10 text-zinc-200 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
                  data-testid="purge-dryrun-btn"
                >
                  {purgeBusy ? 'Loading…' : 'Preview'}
                </Button>
              </div>
              <p className="text-[10px] font-sans text-zinc-500">
                Must contain <span className="font-mono">@</span> and be ≥ 4 chars. Matching is case-insensitive substring.
              </p>
            </div>

            {purgePreview && (
              <div className="bg-rose-500/[0.06] border border-rose-500/20 rounded-sm p-3 space-y-2" data-testid="purge-preview">
                {purgePreview.user_count === 0 ? (
                  <p className="text-sm font-sans text-zinc-400">No staff match this pattern.</p>
                ) : (
                  <>
                    <p className="text-sm font-sans text-rose-200">
                      <strong>{purgePreview.user_count}</strong> staff would be deleted:
                    </p>
                    <div className="max-h-40 overflow-y-auto bg-black/30 border border-white/5 rounded-sm divide-y divide-white/5">
                      {purgePreview.would_delete_users.map((u) => (
                        <div key={u.id} className="px-2.5 py-1.5 text-xs font-sans">
                          <span className="text-white">{u.name || u.email}</span>
                          <span className="text-zinc-500"> · {u.email}</span>
                          <Badge className="ml-2 bg-zinc-500/20 text-zinc-400 border-0 font-sans text-[9px]">{u.role}</Badge>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-5 gap-2 text-[10px] font-sans text-zinc-400 pt-1">
                      <div>Clock: <span className="text-white">{purgePreview.related?.clock_records || 0}</span></div>
                      <div>Shifts: <span className="text-white">{purgePreview.related?.shifts || 0}</span></div>
                      <div>Leave: <span className="text-white">{purgePreview.related?.leave_requests || 0}</span></div>
                      <div>KPI: <span className="text-white">{purgePreview.related?.performance_scores || 0}</span></div>
                      <div>Audit: <span className="text-white">{purgePreview.related?.audit_log || 0}</span></div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => confirmPurge(true)}
                        disabled={purgeBusy}
                        className="flex-1 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-100 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
                        data-testid="purge-confirm-wipe-btn"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        {purgeBusy ? 'Working…' : 'Delete + Wipe History'}
                      </Button>
                      <Button
                        onClick={() => confirmPurge(false)}
                        disabled={purgeBusy}
                        className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
                        data-testid="purge-confirm-keep-btn"
                      >
                        Delete Only · Keep History
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className={`bg-white/[0.03] border border-white/5 rounded-sm p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-sm bg-${color}-500/10 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${color}-400`} />
        </div>
      </div>
      <p className="text-2xl font-sans text-white font-semibold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">{label}</p>
    </div>
  );
}
